/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React from 'react';
import classNames from 'classnames';

/** Context：当前选中 tab、切换回调、可访问性 id 与排列方向。 */
type TabsContextValue = {
  value: string;
  onValueChange: (next: string) => void;
  baseId: string;
  orientation: 'horizontal' | 'vertical';
};

const TabsContext = React.createContext<TabsContextValue | null>(null);

/** 读取 Tabs Context；在根组件外使用会抛错。 */
function useTabsContext(componentName: string): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) {
    throw new Error(`${componentName} 必须在 <Tabs> 内使用`);
  }
  return ctx;
}

export type TabsProps = {
  /** 受控：当前选中的 tab value */
  value?: string;
  /** 非受控：初始选中的 tab value */
  defaultValue?: string;
  /** 选中变化时回调（受控与非受控均会触发） */
  onValueChange?: (value: string) => void;
  /** Tab 排列方向，影响根节点 `data-orientation` 与 tablist 的 `aria-orientation` */
  orientation?: 'horizontal' | 'vertical';
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * Tabs 根容器：组合 TabsList / TabsTrigger / TabsContent 使用；
 * 支持受控（value + onValueChange）与非受控（defaultValue）。
 */
function TabsRoot({
  value: valueProp,
  defaultValue = '',
  onValueChange,
  className,
  children,
  orientation = 'horizontal',
  ...rest
}: TabsProps) {
  const [innerValue, setInnerValue] = React.useState(defaultValue);
  const isControlled = valueProp !== undefined;
  const value = isControlled ? (valueProp as string) : innerValue;

  const handleChange = React.useCallback(
    (next: string) => {
      if (!isControlled) {
        setInnerValue(next);
      }
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const baseId = React.useId();
  const contextValue = React.useMemo(
    () => ({
      value,
      onValueChange: handleChange,
      baseId,
      orientation,
    }),
    [value, handleChange, baseId, orientation],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        className={className}
        data-orientation={orientation}
        {...rest}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export type TabsListProps = React.HTMLAttributes<HTMLDivElement>;

/** tab 触发器容器，对应 role="tablist"；纵向时声明 aria-orientation 供读屏。 */
function TabsListInner(
  { className, ...rest }: TabsListProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { orientation } = useTabsContext('TabsList');
  return (
    <div
      ref={ref}
      role="tablist"
      aria-orientation={orientation === 'vertical' ? 'vertical' : undefined}
      className={classNames(className)}
      {...rest}
    />
  );
}

export const TabsList = React.forwardRef(TabsListInner);

export type TabsTriggerProps = {
  value: string;
  disabled?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

/** 单个 tab 按钮，对应 role="tab"；可用 data-state 做样式。 */
function TabsTriggerInner(
  {
    value,
    disabled,
    className,
    onClick,
    type = 'button',
    ...rest
  }: TabsTriggerProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { value: selected, onValueChange, baseId } = useTabsContext('TabsTrigger');
  const isActive = selected === value;
  const triggerId = `${baseId}-trigger-${value}`;
  const panelId = `${baseId}-content-${value}`;

  return (
    <button
      ref={ref}
      type={type}
      role="tab"
      id={triggerId}
      aria-selected={isActive}
      aria-controls={panelId}
      data-state={isActive ? 'active' : 'inactive'}
      disabled={disabled}
      className={classNames(className)}
      onClick={(e) => {
        if (!disabled) {
          onValueChange(value);
        }
        onClick?.(e);
      }}
      {...rest}
    />
  );
}

export const TabsTrigger = React.forwardRef(TabsTriggerInner);

export type TabsContentProps = {
  value: string;
  /** 为 true 时未选中也会挂载 DOM，仅用 hidden 隐藏（便于动画等场景） */
  forceMount?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

/** 与 TabsTrigger 同 value 关联的面板，对应 role="tabpanel"。 */
function TabsContentInner(
  {
    value,
    forceMount,
    className,
    ...rest
  }: TabsContentProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { value: selected, baseId } = useTabsContext('TabsContent');
  const isActive = selected === value;
  const triggerId = `${baseId}-trigger-${value}`;
  const panelId = `${baseId}-content-${value}`;

  if (!isActive && !forceMount) {
    return null;
  }

  return (
    <div
      ref={ref}
      role="tabpanel"
      id={panelId}
      aria-labelledby={triggerId}
      data-state={isActive ? 'active' : 'inactive'}
      hidden={!isActive}
      className={classNames(className)}
      {...rest}
    />
  );
}

export const TabsContent = React.forwardRef(TabsContentInner);

export { TabsRoot as Tabs };
